import React, { useState } from "react";
import { Download, ExternalLink, Search, BookOpen, Wifi, Bot, Church, Heart } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import ShareButtons from "@/components/ShareButtons";
import { AlbumCompacto } from "@/components/GaleriaFotos";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import Card from "@/components/ui/Card";
import SmartImage from "@/components/ui/SmartImage";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import FilterPills from "@/components/ui/FilterPills";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

const categoryConfig = {
  redes: { label: "Redes Sociales", icon: Wifi, color: "bg-purple-100 text-purple-700" },
  ia: { label: "Inteligencia Artificial", icon: Bot, color: "bg-blue-100 text-blue-700" },
  pastoral: { label: "Pastoral Digital", icon: Church, color: "bg-amber-100 text-amber-700" },
  espiritualidad: { label: "Espiritualidad", icon: Heart, color: "bg-red-100 text-red-700" },
};

const typeLabels = { guia: "Guía", manual: "Manual", plantilla: "Plantilla", documento: "Documento" };

const CATEGORIAS = [
  { value: "todos", label: "Todos" },
  ...Object.entries(categoryConfig).map(([value, { label }]) => ({ value, label })),
];

export default function Recursos() {
  const { data: resources, loading } = useCachedResource("resources", () => apiGet("/resources"));
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("todos");

  const recursos = Array.isArray(resources) ? resources : [];
  const filtered = recursos.filter(r => {
    const matchCat = activeCategory === "todos" || r.category === activeCategory;
    const matchSearch =
      !search ||
      (r.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.description || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div data-testid="recursos-page">
      <SEO
        title="Recursos para la evangelización digital"
        description="Guías, manuales, plantillas y documentos eclesiales descargables para equiparte en la evangelización digital."
      />
      <PageHero slug="recursos" />

      {/* Búsqueda y filtros.
          La barra era `sticky top-16`, un valor fijado a la altura móvil del
          navbar mientras este es md:h-20: en escritorio se pegaba 16 px
          demasiado arriba y quedaba parcialmente debajo. Ahora la distancia
          acompaña a la altura real en cada punto de ruptura. */}
      <section className="py-8 px-6 md:px-12 border-b border-border bg-card sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1 max-w-md w-full">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              aria-hidden="true"
            />
            <label htmlFor="buscar-recursos" className="sr-only">
              Buscar recursos
            </label>
            <input
              id="buscar-recursos"
              data-testid="recursos-search"
              type="search"
              placeholder="Buscar recursos..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-full border border-border bg-background text-sm focus-ring"
            />
          </div>
          <FilterPills
            options={CATEGORIAS}
            value={activeCategory}
            onChange={setActiveCategory}
            label="Filtrar recursos por categoría"
            data-testid="recursos-category-filter"
          />
        </div>
      </section>

      {/* Rejilla de recursos */}
      <Section tone="light" space="tight">
        {loading && !recursos.length ? (
          <CardGridSkeleton count={6} media={false} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No se encontraron recursos"
            description="Prueba con otros filtros o con otros términos de búsqueda."
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => {
              const catConf = categoryConfig[r.category] || {
                label: r.category,
                icon: BookOpen,
                color: "bg-muted text-muted-foreground",
              };
              return (
                <Reveal key={r._id || i} delay={Math.min(i, 5) * 70} distance={24}>
                  <Card data-testid={`resource-card-${i}`} className="p-6 flex flex-col h-full" interactive>
                    {/* La foto se guardaba en `image_url` pero esta página no la
                        pintaba nunca. Sin foto se queda la tarjeta de siempre. */}
                    {r.image_url && (
                      <SmartImage
                        src={r.image_url}
                        alt=""
                        ratio="16 / 9"
                        className="-mx-6 -mt-6 mb-5"
                        data-testid={`resource-image-${i}`}
                      />
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${catConf.color}`}>
                        <catConf.icon className="w-3 h-3" aria-hidden="true" />
                        {catConf.label}
                      </div>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {typeLabels[r.type] || r.type}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-base mb-2">{r.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{r.description}</p>
                    <AlbumCompacto fotos={r.gallery} className="mb-4" />
                    {r.url ? (
                      <CTAButton href={r.url} size="sm" className="font-semibold"
                        data-testid={`resource-download-${i}`}>
                        <Download className="w-3.5 h-3.5" aria-hidden="true" /> Descargar
                      </CTAButton>
                    ) : (
                      <ArrowLink to="/contacto" data-testid={`resource-request-${i}`} className="font-semibold">
                        Solicitar recurso <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                      </ArrowLink>
                    )}
                    <div className="mt-4 pt-3 border-t border-border flex justify-end">
                      <ShareButtons
                        compact
                        url="/recursos"
                        title={r.title}
                        description={r.description || ""}
                      />
                    </div>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        )}
      </Section>

      {/* Invitación a aportar material */}
      <Section tone="muted" width="narrow" space="tight" innerClassName="text-center">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold mb-3">¿Tienes material para compartir?</h2>
          <p className="text-muted-foreground mb-8">
            Si has creado una guía, manual o plantilla evangelizadora que pueda ayudar a otros,
            compártela con la comunidad.
          </p>
          <div className="flex justify-center">
            <CTAButton to="/contacto" className="font-semibold">Enviar recurso</CTAButton>
          </div>
        </Reveal>
      </Section>

      <FloatingEditButton slug="recursos" />
    </div>
  );
}
